import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
    CreateProductPayload,
    UpdateProductPayload,
} from '@/lib/api/product-api'
import { productApi } from '@/lib/api/product-api'

export const productKeys = {
    all: ['products'] as const,
    list: (params?: Record<string, string | number | boolean | undefined>) =>
        [...productKeys.all, 'list', params] as const,
    detail: (id: string) => [...productKeys.all, 'detail', id] as const,
    stockBreakdown: (id: string) =>
        [...productKeys.all, 'stock-breakdown', id] as const,
}

export function useProducts(
    params?: Record<string, string | number | boolean | undefined>,
    enabled = true,
) {
    return useQuery({
        queryKey: productKeys.list(params),
        queryFn: () => productApi.list(params),
        enabled,
    })
}

export function useProduct(id: string) {
    return useQuery({
        queryKey: productKeys.detail(id),
        queryFn: async () => {
            const res = await productApi.getById(id)
            return res.data!
        },
        enabled: !!id,
    })
}

/** Tồn khả dụng gộp theo kho / vị trí / mức CK — thẻ tồn kho màn chi tiết SP. */
export function useProductStockBreakdown(id: string) {
    return useQuery({
        queryKey: productKeys.stockBreakdown(id),
        queryFn: async () => {
            const res = await productApi.getStockBreakdown(id)
            return res.data!
        },
        enabled: !!id,
    })
}

export function useCreateProduct() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (data: CreateProductPayload) => productApi.create(data),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useUpdateProduct() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            id,
            data,
        }: {
            id: string
            data: UpdateProductPayload
        }) => productApi.update(id, data),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useDeleteProduct() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: (id: string) => productApi.delete(id),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useUploadProductImage() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({ productId, file }: { productId: string; file: File }) =>
            productApi.uploadImage(productId, file),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useDeleteProductImage() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            productId,
            imageId,
        }: {
            productId: string
            imageId: string
        }) => productApi.deleteImage(productId, imageId),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useSetPrimaryImage() {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: ({
            productId,
            imageId,
        }: {
            productId: string
            imageId: string
        }) => productApi.setPrimaryImage(productId, imageId),
        onSuccess: () => qc.invalidateQueries({ queryKey: productKeys.all }),
    })
}

export function useImportProducts() {
    return useMutation({
        mutationFn: (file: File) => productApi.importExcel(file),
    })
}
